import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import { useToast } from 'vuestic-ui'

// Instancia limpia de axios para uso en la app
const axiosInstance: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
})

// Keys usadas en localStorage
const ACCESS_KEY = 'access_token'
const REFRESH_KEY = 'refresh_token'

export const tokenService = {
  getAccessToken: (): string | null => {
    try {
      return localStorage.getItem(ACCESS_KEY)
    } catch (e) {
      return null
    }
  },
  getRefreshToken: (): string | null => {
    try {
      return localStorage.getItem(REFRESH_KEY)
    } catch (e) {
      return null
    }
  },
  setTokens: (accessToken: string | null, refreshToken?: string | null) => {
    try {
      if (accessToken) {
        localStorage.setItem(ACCESS_KEY, accessToken)
      } else {
        localStorage.removeItem(ACCESS_KEY)
      }

      if (typeof refreshToken !== 'undefined') {
        if (refreshToken) {
          localStorage.setItem(REFRESH_KEY, refreshToken)
        } else if (refreshToken === null) {
          localStorage.removeItem(REFRESH_KEY)
        }
      }
    } catch (e) {
      // ignore
    }
  },
  clearTokens: () => {
    try {
      localStorage.removeItem(ACCESS_KEY)
      localStorage.removeItem(REFRESH_KEY)
    } catch (e) {
      // ignore
    }
  },
}

// Request interceptor: adjuntar Authorization si existe token
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = tokenService.getAccessToken()
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error: AxiosError) => Promise.reject(error),
)

// Response interceptor: manejo de refresh en 401 y toasts para errores comunes
let isRefreshing = false
let failedQueue: Array<{ resolve: (value?: unknown) => void; reject: (reason?: any) => void }> = []

const processQueue = (error: any = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error)
    else prom.resolve()
  })
  failedQueue = []
}

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
    const { init } = useToast()

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(() => axiosInstance(originalRequest))
          .catch((err) => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const refresh_token = tokenService.getRefreshToken()
        if (!refresh_token) throw new Error('No refresh token available')

        const base = axiosInstance.defaults.baseURL || ''
        const url = `${base.replace(/\/$/, '')}/auth/refresh`
        const response = await axios.post(url, { refresh_token })
        const access_token = response?.data?.access_token
        const returned_refresh = response?.data?.refresh_token

        if (!access_token) throw new Error('No access token returned from refresh')

        tokenService.setTokens(access_token, returned_refresh ?? null)
        isRefreshing = false
        processQueue()
        if (originalRequest.headers && access_token) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`
        }
        return axiosInstance(originalRequest)
      } catch (refreshError: any) {
        processQueue(refreshError)
        isRefreshing = false

        // Si el refresh falla, limpiar tokens
        try {
          tokenService.clearTokens()
        } catch (e) {
          void e
        }

        // No mostrar mensaje agresivo - dejar que cada componente maneje su propio error
        return Promise.reject(refreshError)
      }
    }

    // Otros errores: mostrar toast según status
    if (error.response) {
      const status = error.response.status
      let message = error.message
      try {
        message = (error.response.data as any)?.message || error.message
      } catch {
        message = 'Error en la respuesta del servidor'
      }

      switch (status) {
        case 403:
          init({ message: 'No tienes permisos para realizar esta acción', color: 'danger' })
          break
        case 404:
          init({ message: 'Recurso no encontrado', color: 'warning' })
          break
        case 422: {
          try {
            const errors = (error.response.data as any)?.errors
            if (errors) {
              const firstError = Object.values(errors)[0] as string[]
              init({ message: firstError[0], color: 'danger' })
            }
          } catch {
            init({ message: message || 'Error de validación', color: 'danger' })
          }
          break
        }
        case 500:
          init({ message: 'Error interno del servidor', color: 'danger' })
          break
        default:
          if (status !== 401) init({ message: message || 'Ha ocurrido un error', color: 'danger' })
      }
    } else if (error.request) {
      init({ message: 'No se pudo conectar con el servidor', color: 'danger' })
    }

    return Promise.reject(error)
  },
)

export default axiosInstance
